import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, CircularProgress, Typography } from '@mui/material';
import { loginSuccess, logout } from '../../store/slices/authSlice';
import { RootState } from '../../store/store';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8009';

/**
 * Landing page after GitHub App installation.
 * GitHub redirects here as:
 *   /auth/github-installed?installation_id=xxx&setup_action=install
 *
 * We read the installation_id directly from the URL and save it to the
 * backend — no webhook required for the initial install.
 *
 * If installation_id is missing (e.g. user navigated here directly),
 * we fall back to polling /auth/me until it appears via webhook.
 */
export const GitHubInstalledPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { accessToken } = useSelector((state: RootState) => state.auth);
  const handled = useRef(false);
  const [message, setMessage] = useState('Completing GitHub setup…');

  useEffect(() => {
    if (handled.current) return;

    if (!accessToken) {
      navigate('/auth/login', { replace: true });
      return;
    }

    const installationId = searchParams.get('installation_id');

    if (installationId) {
      // Fast path: GitHub gave us the installation_id in the URL
      handled.current = true;
      saveInstallation(installationId);
    } else {
      // Fallback: poll /auth/me (webhook may have already fired)
      pollForInstallation();
    }

    async function saveInstallation(id: string) {
      try {
        const res = await fetch(`${API_BASE_URL}/auth/me/installation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ installation_id: id }),
        });

        if (res.status === 401) {
          handleAuthFailure();
          return;
        }

        if (!res.ok) throw new Error('save failed');

        const user = await res.json();
        localStorage.setItem('user', JSON.stringify(user));
        dispatch(loginSuccess({ user, accessToken: accessToken! }));
        navigate('/dashboard', { replace: true });
      } catch {
        setMessage('Something went wrong. Redirecting to dashboard…');
        setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
      }
    }

    async function pollForInstallation(attempt = 0) {
      if (attempt >= 8) {
        setMessage('Setup is taking longer than expected. Continuing to dashboard…');
        setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
        return;
      }

      try {
        const res = await fetch(`${API_BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        if (res.status === 401) {
          handleAuthFailure();
          return;
        }

        const user = await res.json();

        if (user.installation_id) {
          localStorage.setItem('user', JSON.stringify(user));
          dispatch(loginSuccess({ user, accessToken: accessToken! }));
          navigate('/dashboard', { replace: true });
          return;
        }
      } catch {
        // network hiccup — keep polling
      }

      setTimeout(() => pollForInstallation(attempt + 1), 1500);
    }

    function handleAuthFailure() {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      dispatch(logout());
      navigate('/auth/login', { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography color="text.secondary">{message}</Typography>
    </Box>
  );
};
